import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  FeatureGraceBackfillResult,
  FeatureMatrix,
  FeatureMatrixCell,
  ModuleBenefit,
  PaymentProcessor,
  Plan,
  PlanCatalogEntry,
  PlanPricing,
  PlanPricingChangeAmountRequest,
  PlanPricingUpsertRequest,
  PlanStripeProduct,
  PlanUpsertRequest,
  StripePriceCheckRequest,
  StripePriceCheckResult,
  StripeStatus
} from '../models/plan.model';

@Injectable({ providedIn: 'root' })
export class PlanService {
  private readonly baseUrl = environment.subscriptionApiUrl + '/api/Plans';

  constructor(private http: HttpClient) {}

  /** All plans (active and inactive) with limits, for admin management. */
  getPlans(): Observable<Plan[]> {
    return this.http.get<Plan[]>(`${this.baseUrl}/admin`);
  }

  /** Single plan with its included features (planFeatures). */
  getPlan(planId: number): Observable<Plan> {
    return this.http.get<Plan>(`${this.baseUrl}/${planId}`);
  }

  createPlan(request: PlanUpsertRequest): Observable<Plan> {
    return this.http.post<Plan>(this.baseUrl, request);
  }

  updatePlan(planId: number, request: PlanUpsertRequest): Observable<Plan> {
    return this.http.put<Plan>(`${this.baseUrl}/${planId}`, request);
  }

  setPlanActive(planId: number, isActive: boolean): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${planId}/status`, { isActive });
  }

  deletePlan(planId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${planId}`);
  }

  /** Replace the plan's included feature set. */
  setPlanFeatures(planId: number, featureIds: number[]): Observable<Plan> {
    return this.http.put<Plan>(`${this.baseUrl}/${planId}/features`, { featureIds });
  }

  // ── Plan pricing (dbo.PlanPricing) ──────────────────────────────────────
  // The amounts customers are quoted and charged. A plan can carry several rows:
  // one per country / billing interval / processor / live-or-test mode.

  /** Processors that prices can be attached to (Stripe, Paystack, …). */
  getProcessors(): Observable<PaymentProcessor[]> {
    return this.http.get<PaymentProcessor[]>(`${this.baseUrl}/processors`);
  }

  getPlanPricing(planId: number): Observable<PlanPricing[]> {
    return this.http.get<PlanPricing[]>(`${this.baseUrl}/${planId}/pricing`);
  }

  createPlanPricing(planId: number, request: PlanPricingUpsertRequest): Observable<PlanPricing> {
    return this.http.post<PlanPricing>(`${this.baseUrl}/${planId}/pricing`, request);
  }

  updatePlanPricing(planId: number, pricingId: string, request: PlanPricingUpsertRequest): Observable<PlanPricing> {
    return this.http.put<PlanPricing>(`${this.baseUrl}/${planId}/pricing/${pricingId}`, request);
  }

  setPlanPricingActive(planId: number, pricingId: string, isActive: boolean): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${planId}/pricing/${pricingId}/status`, { isActive });
  }

  deletePlanPricing(planId: number, pricingId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${planId}/pricing/${pricingId}`);
  }

  // ── Stripe prices made from here ────────────────────────────────────────
  // Stripe rows are created in (or checked against) this environment's Stripe account by the
  // API; the amount of a Stripe price never changes in place - change-amount adds a new row.

  /** Whether this environment has a Stripe key, and which mode it and the checkout are in. */
  getStripeStatus(): Observable<StripeStatus> {
    return this.http.get<StripeStatus>(`${this.baseUrl}/stripe/status`);
  }

  /** Looks a Stripe price up and compares it with the row about to be saved. */
  checkStripePrice(planId: number, request: StripePriceCheckRequest): Observable<StripePriceCheckResult> {
    return this.http.post<StripePriceCheckResult>(`${this.baseUrl}/${planId}/pricing/stripe/check`, request);
  }

  /** The plan's Stripe product in this environment's mode (both fields null when it has none yet). */
  getPlanStripeProduct(planId: number): Observable<PlanStripeProduct> {
    return this.http.get<PlanStripeProduct>(`${this.baseUrl}/${planId}/stripe/product`);
  }

  /** New Stripe price on a new row from startsOnUtc; the old row ends then. Returns the new row. */
  changePlanPricingAmount(
    planId: number,
    pricingId: string,
    request: PlanPricingChangeAmountRequest
  ): Observable<PlanPricing> {
    return this.http.post<PlanPricing>(`${this.baseUrl}/${planId}/pricing/${pricingId}/change-amount`, request);
  }

  // ── Modules: catalog (dbo.PlanCatalog) and the plans × features matrix ──────

  /** Every catalog row, including rows with no plan (coming soon, bundle-only). */
  getCatalog(): Observable<PlanCatalogEntry[]> {
    return this.http
      .get<unknown[]>(`${this.baseUrl}/catalog`)
      .pipe(map((rows) => (rows || []).map(normalizeCatalogEntry)));
  }

  /** Create or replace the catalog row for a module key. */
  saveCatalogEntry(entry: PlanCatalogEntry): Observable<PlanCatalogEntry> {
    // Benefits go as {title, detail} objects and capabilities as strings; the API serialises
    // them into BenefitsJson / CapabilitiesJson itself.
    const body = { ...entry, benefits: entry.benefits || [], capabilities: entry.capabilities || [] };
    return this.http
      .put<unknown>(`${this.baseUrl}/catalog/${encodeURIComponent(entry.moduleKey)}`, body)
      .pipe(map((row) => (row ? normalizeCatalogEntry(row) : entry)));
  }

  deleteCatalogEntry(moduleKey: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/catalog/${encodeURIComponent(moduleKey)}`);
  }

  getFeatureMatrix(): Observable<FeatureMatrix> {
    return this.http.get<FeatureMatrix>(`${this.baseUrl}/feature-matrix`);
  }

  /** Writes each cell's IsIncluded explicitly (never NULL). Send only the changed cells. */
  saveFeatureMatrix(cells: FeatureMatrixCell[]): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/feature-matrix`, { cells });
  }

  /**
   * One-off at the Events gate's release (free-plan-marketplace FR-4.5): every business whose
   * plan lacks Events & Ticketing keeps Events until its last already-scheduled event ends.
   * Idempotent; asks Retail.API once per business, so it can take a while.
   */
  backfillEventsGrace(): Observable<FeatureGraceBackfillResult> {
    return this.http.post<FeatureGraceBackfillResult>(
      `${environment.subscriptionApiUrl}/api/admin/feature-grace/events/backfill`,
      {}
    );
  }
}

/** A benefit as stored: a {title, detail} object, or (rows before the promo copy) a plain string. */
export function normalizeBenefit(raw: unknown): ModuleBenefit | null {
  if (typeof raw === 'string') {
    return raw.trim() ? { title: raw.trim(), detail: '' } : null;
  }
  if (raw && typeof raw === 'object') {
    const o = raw as { title?: unknown; detail?: unknown; Title?: unknown; Detail?: unknown };
    const title = String(o.title ?? o.Title ?? '').trim();
    return title ? { title, detail: String(o.detail ?? o.Detail ?? '').trim() } : null;
  }
  return null;
}

/** An array field that may arrive parsed or as its raw *Json column. */
function jsonArray(parsed: unknown, json: unknown): unknown[] {
  if (Array.isArray(parsed)) {
    return parsed;
  }
  if (typeof json === 'string' && json.trim()) {
    try {
      const value = JSON.parse(json);
      return Array.isArray(value) ? value : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Tolerates the API's casing and the stored shape: PlanID may serialise as planID, and the
 * benefits may arrive as the raw BenefitsJson string rather than a parsed array.
 */
export function normalizeCatalogEntry(raw: any): PlanCatalogEntry {
  const benefits = jsonArray(raw?.benefits, raw?.benefitsJson)
    .map(normalizeBenefit)
    .filter((b): b is ModuleBenefit => b !== null);
  const capabilities = jsonArray(raw?.capabilities, raw?.capabilitiesJson)
    .filter((c): c is string => typeof c === 'string' && !!c.trim())
    .map((c) => c.trim());
  const planId = raw?.planId ?? raw?.planID ?? null;
  return {
    moduleKey: raw?.moduleKey ?? '',
    planId: planId === null || planId === undefined ? null : Number(planId),
    featureName: raw?.featureName ?? null,
    bundleOffer: raw?.bundleOffer ?? null,
    displayName: raw?.displayName ?? raw?.moduleKey ?? '',
    icon: raw?.icon ?? null,
    pitch: raw?.pitch ?? null,
    headline: raw?.headline ?? null,
    subhead: raw?.subhead ?? null,
    capabilities,
    benefits,
    screenshotUrl: raw?.screenshotUrl ?? null,
    sidebarGroup: raw?.sidebarGroup ?? null,
    sortOrder: Number(raw?.sortOrder ?? 0),
    isVisible: raw?.isVisible !== false,
    isPurchasable: raw?.isPurchasable !== false,
    updatedAt: raw?.updatedAt ?? null
  };
}
